/**
 * TreatX Admin Authentication Context
 * 
 * UNIFIED AUTH ONLY — derives admin state from:
 *   localStorage.treatx_unified_token  (JWT)
 *   localStorage.treatx_unified_user   (user payload with role)
 *
 * Admin roles: treatx_admin, admin, platform_admin, superadmin
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getUnifiedAuth, isTreatxAdmin, clearUnifiedAuth } from '../utils/unifiedAuth';

const ADMIN_ROLES = ['treatx_super_admin', 'treatx_admin'];

const TreatXAdminAuthContext = createContext();

export const useTreatXAdminAuth = () => {
  const context = useContext(TreatXAdminAuthContext);
  if (!context) {
    throw new Error('useTreatXAdminAuth must be used within TreatXAdminAuthProvider');
  }
  return context;
};

export const TreatXAdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(() => {
    const { user } = getUnifiedAuth();
    if (user && ADMIN_ROLES.includes(user.role)) return user;
    return null;
  });

  const isAuthenticated = !!admin;

  // Sync state when unified auth changes (e.g., login/logout in another component)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'treatx_unified_user' || e.key === 'treatx_unified_token') {
        const { user } = getUnifiedAuth();
        setAdmin(user && ADMIN_ROLES.includes(user.role) ? user : null);
      }
    };
    window.addEventListener('storage', handleStorage);

    // Also poll for same-tab changes (login triggers don't fire storage events)
    const interval = setInterval(() => {
      const shouldBeAdmin = isTreatxAdmin();
      if (shouldBeAdmin && !admin) {
        const { user } = getUnifiedAuth();
        setAdmin(user);
      } else if (!shouldBeAdmin && admin) {
        setAdmin(null);
      }
    }, 1000);

    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, [admin]);

  const logoutAdmin = useCallback(() => {
    clearUnifiedAuth();
    setAdmin(null);
  }, []);

  const value = {
    admin,
    isAuthenticated,
    loading: false,
    loginAdmin: async () => ({ success: false, error: 'Use unified login at /login' }),
    logoutAdmin,
    logout: logoutAdmin,
  };

  return (
    <TreatXAdminAuthContext.Provider value={value}>
      {children}
    </TreatXAdminAuthContext.Provider>
  );
};

export default TreatXAdminAuthContext;
